import { Plus, Search, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import client from "../../api/client";
import {
	Button,
	Input,
	Modal,
	Notification,
	Select,
	Table,
	TextArea,
	useNotification,
} from "../../components/FormComponents";
import Pagination from "../../components/Pagination";
import { ICON_OPTIONS } from "../../constants/themes";
import { useCrudPage } from "../../hooks/useCrudPage";
import { planSchema } from "../../schemas/planSchema";

export default function Plans() {
	const { notification, showNotification } = useNotification();

	const {
		data: plans,
		loading,
		saving,
		page,
		limit,
		total,
		search,
		isModalOpen,
		editingId,
		formData,
		errors,
		setPage,
		setSearch,
		updateField,
		handleOpenModal: crudOpen,
		handleCloseModal,
		handleSubmit,
		handleDelete,
		setFormData,
	} = useCrudPage("/plans", planSchema, {
		onSuccess: (action) => {
			const messages = {
				created: "Plano criado com sucesso!",
				updated: "Plano atualizado com sucesso!",
				deleted: "Plano deletado com sucesso!",
			};
			showNotification(messages[action]);
		},
		defaultFormData: {
			company_id: "",
			name: "",
			value: "",
			description: "",
			icon: "server",
			subtitle: "",
			image_url: "",
			features: [],
		},
	});

	const [companies, setCompanies] = useState([]);

	const fetchCompanies = useCallback(async () => {
		try {
			const response = await client.get("/companies/all");
			setCompanies(response.data);
		} catch (error) {
			console.error("Error fetching companies:", error);
		}
	}, []);

	useEffect(() => {
		fetchCompanies();
	}, [fetchCompanies]);

	const handleOpenModal = (planData = null) => {
		if (planData) {
			const features =
				typeof planData.features === "string"
					? JSON.parse(planData.features)
					: planData.features || [];
			crudOpen({ ...planData, features });
		} else {
			crudOpen();
		}
	};

	const columns = [
		{
			key: "company_name",
			label: "Empresa",
			render: (val) => <span className="font-medium text-cyan-300">{val}</span>,
		},
		{ key: "name", label: "Nome" },
		{
			key: "value",
			label: "Valor",
			render: (val) => (
				<span className="text-green-400 font-bold">
					R$ {parseFloat(val).toFixed(2)}
				</span>
			),
		},
		{ key: "description", label: "Descrição" },
	];

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-white">Gerenciar Planos</h1>
				<Button type="button" onClick={() => handleOpenModal()}>
					<Plus className="w-4 h-4 inline mr-2" />
					Novo Plano
				</Button>
			</div>

			{/* Search */}
			<div className="relative">
				<Search
					className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
					aria-hidden="true"
				/>
				<input
					type="text"
					value={search}
					onChange={(e) => {
						setSearch(e.target.value);
						setPage(1);
					}}
					placeholder="Buscar planos..."
					aria-label="Buscar planos"
					className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-white/10 rounded-lg text-slate-300 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
				/>
			</div>

			<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
				<Table
					columns={columns}
					data={plans}
					loading={loading}
					onEdit={handleOpenModal}
					onDelete={(id) =>
						handleDelete(id, "Tem certeza que deseja deletar este plano?")
					}
				/>

				<Pagination
					page={page}
					limit={limit}
					total={total}
					onPageChange={setPage}
				/>
			</div>

			<Modal
				isOpen={isModalOpen}
				title={editingId ? "Editar Plano" : "Novo Plano"}
				onClose={handleCloseModal}
				onSubmit={handleSubmit}
				submitLabel={editingId ? "Atualizar" : "Criar"}
				loading={saving}
			>
				<div className="space-y-4">
					<Select
						label="Empresa *"
						value={formData.company_id || ""}
						onChange={(e) => updateField("company_id", e.target.value)}
						options={companies.map((c) => ({ value: c.id, label: c.name }))}
						error={errors.company_id}
					/>
					<Input
						label="Nome do Plano *"
						value={formData.name || ""}
						onChange={(e) => updateField("name", e.target.value)}
						placeholder="Ex: Start, Professional, Enterprise"
						error={errors.name}
					/>
					<Input
						label="Valor Mensal *"
						type="number"
						step="0.01"
						value={formData.value || ""}
						onChange={(e) => updateField("value", e.target.value)}
						placeholder="0.00"
						error={errors.value}
					/>
					<TextArea
						label="Descrição"
						value={formData.description || ""}
						onChange={(e) => updateField("description", e.target.value)}
						placeholder={
							"Descrição do plano para exibir na proposta.\nUse linhas com - para listar features:\n- Hospedagem Cloud\n- SSL Gratuito\n- Suporte 24/7"
						}
						rows="5"
					/>

					{/* Visual Fields */}
					<div className="border-t border-slate-700 pt-4 mt-2">
						<p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
							Campos Visuais (Proposta)
						</p>
						<div className="grid grid-cols-2 gap-3">
							<Select
								label="Ícone"
								value={formData.icon || "server"}
								onChange={(e) => updateField("icon", e.target.value)}
								options={ICON_OPTIONS.filter((o) => o.value)}
							/>
							<Input
								label="Subtítulo"
								value={formData.subtitle || ""}
								onChange={(e) => updateField("subtitle", e.target.value)}
								placeholder="Ex: Melhor custo-benefício"
							/>
						</div>
						<Input
							label="URL da Imagem (opcional)"
							value={formData.image_url || ""}
							onChange={(e) => updateField("image_url", e.target.value)}
							placeholder="https://..."
						/>
						{formData.image_url && (
							<div className="mt-1 rounded border border-white/5 overflow-hidden bg-slate-800/50 max-h-24">
								<img
									src={formData.image_url}
									alt="Preview"
									className="w-full h-full object-cover"
								/>
							</div>
						)}
					</div>

					{/* Features Builder */}
					<div className="border-t border-slate-700 pt-4 mt-2">
						<div className="flex items-center justify-between mb-2">
							<p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
								Features Estruturadas ({(formData.features || []).length})
							</p>
							<button
								type="button"
								onClick={() =>
									setFormData({
										...formData,
										features: [
											...(formData.features || []),
											{ icon: "check", title: "", description: "" },
										],
									})
								}
								className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
							>
								<Plus className="w-3 h-3" aria-hidden="true" /> Adicionar
							</button>
						</div>
						<p className="text-[10px] text-slate-600 mb-2">
							Features com ícone e título aparecem na proposta em vez dos
							bullets de texto.
						</p>
						{(formData.features || []).map((feat, fi) => (
							<div
								key={feat.id || `plan-feature-${fi}`}
								className="p-2 bg-slate-900/50 rounded border border-slate-700/30 mb-2"
							>
								<div className="flex items-center justify-between mb-1">
									<span className="text-[10px] text-slate-500">
										Feature {fi + 1}
									</span>
									<button
										type="button"
										onClick={() =>
											setFormData({
												...formData,
												features: formData.features.filter((_, i) => i !== fi),
											})
										}
										className="text-slate-500 hover:text-red-400 p-1"
										aria-label="Remover feature"
									>
										<X className="w-3 h-3" />
									</button>
								</div>
								<div className="grid grid-cols-[80px_1fr] gap-2">
									<Select
										label="Ícone"
										value={feat.icon || ""}
										onChange={(e) => {
											const updated = [...formData.features];
											updated[fi] = { ...updated[fi], icon: e.target.value };
											setFormData({ ...formData, features: updated });
										}}
										options={ICON_OPTIONS.filter((o) => o.value)}
									/>
									<Input
										label="Título"
										value={feat.title || ""}
										onChange={(e) => {
											const updated = [...formData.features];
											updated[fi] = { ...updated[fi], title: e.target.value };
											setFormData({ ...formData, features: updated });
										}}
										placeholder="Título da feature"
									/>
								</div>
								<Input
									label="Descrição"
									value={feat.description || ""}
									onChange={(e) => {
										const updated = [...formData.features];
										updated[fi] = {
											...updated[fi],
											description: e.target.value,
										};
										setFormData({ ...formData, features: updated });
									}}
									placeholder="Descrição curta..."
								/>
							</div>
						))}
					</div>
				</div>
			</Modal>
		</div>
	);
}
